/**
 * AppShell — main layout with sidebar navigation + terminal command palette.
 *
 * White paper. Black ink. Allen & Co.
 * Quiet sidebar, editorial spacing, monochrome throughout.
 *
 * V5+ lineage: includes intake (work packet), approval inbox,
 * register (proof), surfaces (cross-surface coherence), and the
 * terminal command palette (⌘K).
 * The terminal is the portal's command rail — every view and the agent
 * are reachable from the keyboard.
 *
 * Surface-aware: the shell detects which surface it's running on
 * (browser, desktop, extension) and adjusts visible views accordingly.
 */

import { useState, useEffect, ReactNode, useCallback } from "react";
import { applyMembrane } from "../lib/api-clients";
import { TerminalPalette } from "./TerminalPalette";
import { detectSurface, getSurfaceCapabilities, type SurfaceCapability } from "../lib/surface-context";

export type ViewKey =
  | "dashboard"
  | "work-packet"
  | "agent-chat"
  | "offers"
  | "campaigns"
  | "highlights"
  | "sequences"
  | "contacts"
  | "integrations"
  | "surfaces"
  | "register"
  | "inbox"
  | "api-keys"
  | "conveyance"
  | "profile"
  | "community"
  | "settings"
  | "operator-fleet"
  | "operator-campaigns";

interface NavItem {
  key: ViewKey;
  label: string;
  icon: string;
  operatorOnly?: boolean;
  capability?: SurfaceCapability;
}

// Nav items grouped by frontier stage — surface-aware
const NAV_ITEMS: NavItem[] = [
  { key: "dashboard", label: "Your Spec", icon: "◆", capability: "dashboard" },
  { key: "work-packet", label: "Work Packet", icon: "▶" },
  { key: "agent-chat", label: "Agent", icon: "●", capability: "chat" },
  { key: "offers", label: "Offers", icon: "◇", capability: "offers" },
  { key: "campaigns", label: "Campaigns", icon: "▸", capability: "campaigns" },
  { key: "highlights", label: "Highlights", icon: "★" },
  { key: "sequences", label: "Sequences", icon: "↻" },
  { key: "contacts", label: "Contacts", icon: "○" },
  { key: "integrations", label: "Integrations", icon: "⬡" },
  { key: "surfaces", label: "Surfaces", icon: "◐" },
  { key: "register", label: "Register", icon: "▣", capability: "register" },
  { key: "inbox", label: "Inbox", icon: "!" },
  { key: "api-keys", label: "API Keys", icon: "🔑" },
  { key: "conveyance", label: "Conveyance", icon: "▤", capability: "conveyance" },
  { key: "profile", label: "Profile", icon: "◻", capability: "profiles" },
  { key: "community", label: "Community", icon: "◎", capability: "community" },
  { key: "settings", label: "Settings", icon: "⚙", capability: "settings" },
  { key: "operator-fleet", label: "Fleet", icon: "◈", operatorOnly: true, capability: "fleet" },
  { key: "operator-campaigns", label: "Cross-Client", icon: "◎", operatorOnly: true },
];

interface AppShellProps {
  activeView: ViewKey;
  onViewChange: (view: ViewKey) => void;
  isOperator: boolean;
  onSignOut: () => void;
  pendingCount?: number;
  children: ReactNode;
}

export function AppShell({ activeView, onViewChange, isOperator, onSignOut, pendingCount = 0, children }: AppShellProps) {
  const membraneBypass = isOperator;
  const surface = detectSurface();
  const surfaceCaps = getSurfaceCapabilities();

  // Filter nav items: operator-only items + surface-aware capability gating
  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.operatorOnly && !isOperator) return false;
    // On desktop/extension, only show items with matching capabilities
    if (surface !== "browser" && item.capability && !surfaceCaps.includes(item.capability)) {
      return false;
    }
    return true;
  });

  const [terminalOpen, setTerminalOpen] = useState(false);

  // ⌘K / Ctrl+K → toggle terminal
  const toggleTerminal = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      setTerminalOpen(prev => !prev);
    }
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "K") {
      e.preventDefault();
      setTerminalOpen(prev => !prev);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", toggleTerminal);
    return () => window.removeEventListener("keydown", toggleTerminal);
  }, [toggleTerminal]);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark">R</div>
          <div className="brand-text">
            <div className="brand-name">GetRida</div>
            <div className="brand-tagline">
              {applyMembrane("Inhabited Spec", membraneBypass)}
            </div>
          </div>
        </div>

        {/* Surface indicator — shows which surface you're on */}
        {surface !== "browser" && (
          <div className="surface-indicator">
            <span className="surface-indicator-icon">
              {surface === "desktop" ? "▣" : "◈"}
            </span>
            <span className="surface-indicator-text">{surface}</span>
          </div>
        )}

        <nav className="sidebar-nav">
          {visibleItems.map((item) => (
            <button
              key={item.key}
              className={`nav-item ${activeView === item.key ? "active" : ""}`}
              onClick={() => onViewChange(item.key)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{applyMembrane(item.label, membraneBypass)}</span>
              {item.key === "inbox" && pendingCount > 0 && (
                <span className="nav-badge">{pendingCount}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button
            className="terminal-trigger"
            onClick={() => setTerminalOpen(true)}
            title="Open terminal (⌘K)"
          >
            <span className="mono">⌘K</span> Terminal
          </button>
          <div className="sidebar-footer-row">
            <button className="signout-btn" onClick={onSignOut}>
              Sign out
            </button>
            {isOperator && <div className="role-badge">Op</div>}
          </div>
        </div>
      </aside>

      <main className="main-content">
        {children}
      </main>

      <TerminalPalette
        open={terminalOpen}
        onClose={() => setTerminalOpen(false)}
        onNavigate={onViewChange}
        isOperator={isOperator}
      />
    </div>
  );
}
